import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { TimeField } from "@/components/shared/TimeField";
import { Users, ChevronDown, Search, Check, RotateCcw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ABSENCE_TYPES, ADVANCE_TYPES, typeOf, weekdaysBetween, daysWord, czShort, todayIso } from "@/lib/absences";

// Jeden formulář pro žádost pracovníka i zápis vedením (176).
// mode="request": pracovník za sebe → žádost čeká na schválení.
// mode="manager": vedení za jednoho i víc lidí → rovnou schváleno.
export function AbsenceDialog({ open, onClose, mode = "request", people = [], initial, onSaved, topSlot }) {
  const manager = mode === "manager";
  const self = mode === "self";   // vedení samo sobě — uloží se rovnou schválené
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    const from = initial?.date_from || todayIso();
    setM({ user_ids: initial?.user_ids || [], date_from: from, date_to: initial?.date_to || from,
      type: initial?.type || (mode === "request" && from <= todayIso() ? "lekar" : "dovolena"), part: "full", half: "am", t_from: "07:00", t_to: "09:00", note: "", makeup: false });
    setPickOpen(manager && !(initial?.user_ids || []).length);
    setQ("");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const single = m && m.date_from === m.date_to;
  const list = useMemo(() => {
    const n = q.trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    return people.filter((p) => !n || (p.name || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").includes(n));
  }, [people, q]);
  if (!open || !m) return null;

  const set = (patch) => setM((s) => {
    const next = { ...s, ...patch };
    if (next.date_to < next.date_from) next.date_to = next.date_from;
    if (next.date_from !== next.date_to && next.part !== "full") next.part = "full";
    // Pracovník zpětně nesmí dovolenou ani náhradní volno — přepne na lékaře.
    if (mode === "request" && next.date_from <= todayIso() && ADVANCE_TYPES.includes(next.type)) next.type = "lekar";
    return next;
  });
  const toggle = (id) => set({ user_ids: m.user_ids.includes(id) ? m.user_ids.filter((x) => x !== id) : [...m.user_ids, id] });
  const t = typeOf(m.type);
  const retro = mode === "request" && m.date_from <= todayIso();
  const wd = weekdaysBetween(m.date_from, m.date_to);
  const whenTxt = single ? czShort(m.date_from) : `${czShort(m.date_from)}–${czShort(m.date_to)}`;
  const amount = m.part === "half" ? "půl dne" : m.part === "time" ? `${m.t_from}–${m.t_to}` : daysWord(wd);
  const names = people.filter((p) => m.user_ids.includes(p.id)).map((p) => p.name);
  const pickLabel = names.length === 0 ? "Vyberte lidi" : names.length <= 2 ? names.join(", ") : `${names.slice(0, 2).join(", ")} a další ${names.length - 2}`;
  // 413: neplacené volno, které si člověk napracuje jindy v měsíci — fond se nesníží
  const makeup = m.type === "neplacene_volno" && m.makeup;
  const MONTH_LOC = ["lednu", "únoru", "březnu", "dubnu", "květnu", "červnu", "červenci", "srpnu", "září", "říjnu", "listopadu", "prosinci"];
  const monthLoc = MONTH_LOC[Number(m.date_from.slice(5, 7)) - 1];
  const primary = manager || self ? "Zapsat" : m.type === "nemoc" ? "Nahlásit nemoc" : "Odeslat žádost";

  const submit = async () => {
    if (manager && !m.user_ids.length) return toast.error("Vyberte aspoň jednoho člověka.");
    if (m.part === "full" && wd === 0) return toast.error("V označených dnech není žádný pracovní den.");
    if (m.part === "time" && (!m.t_from || !m.t_to || m.t_to <= m.t_from)) return toast.error("Čas do musí být po čase od.");
    setBusy(true);
    try {
      const body = { type: m.type, date_from: m.date_from, date_to: m.date_to, part: m.part, note: m.note.trim() || null };
      if (m.part === "half") body.half = m.half;
      if (m.part === "time") { body.t_from = m.t_from; body.t_to = m.t_to; }
      if (manager) body.user_ids = m.user_ids;
      if (makeup) body.makeup = true;
      await api.post("/absences", body);
      toast.success(manager ? `Zapsáno: ${t.label.toLowerCase()} · ${plural(m.user_ids.length)}.` : self ? `Zapsáno: ${t.label.toLowerCase()}.` : m.type === "nemoc" ? "Nemoc nahlášena vedení." : "Žádost odeslána ke schválení.");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const seg = (opts, value, onChange, disabled) => (
    <div className="flex p-1 rounded-full bg-secondary gap-1">
      {opts.map((o) => (
        <button key={o.value} type="button" disabled={disabled?.(o.value)} onClick={() => onChange(o.value)} data-testid={`abs-${o.value}`}
          className={`flex-1 h-9 rounded-full text-[13px] font-bold whitespace-nowrap px-2 transition-colors disabled:opacity-40 ${value === o.value ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
          {o.label}
        </button>
      ))}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent sheet className="md:max-w-lg md:max-h-[90vh] overflow-y-auto max-md:pt-8" data-testid="absence-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{manager || self ? "Zapsat nepřítomnost" : "Nahlásit nepřítomnost"}</DialogTitle>
          <DialogDescription>{manager ? "Zapisuje vedení — schváleno rovnou, člověk dostane upozornění." : self ? "Vaše vlastní nepřítomnost — uloží se rovnou jako schválená." : "Žádost půjde ke schválení vedení."}</DialogDescription>
        </DialogHeader>
        {topSlot}
        <div className="space-y-4">
          {manager && (
            <div>
              <Label>Lidé</Label>
              <button type="button" onClick={() => setPickOpen((o) => !o)} data-testid="abs-people-btn"
                className="mt-1 w-full h-11 px-3 rounded-[12px] border border-border bg-card flex items-center gap-2 text-left font-semibold">
                <Users size={16} className="shrink-0 text-muted-foreground" />
                <span className={`flex-1 min-w-0 truncate ${names.length ? "" : "text-muted-foreground"}`}>{pickLabel}</span>
                {names.length > 0 && <span className="rounded-full bg-primary text-primary-foreground px-2 text-xs font-bold tabular-nums">{names.length}</span>}
                <ChevronDown size={16} className={`shrink-0 transition-transform motion-reduce:transition-none ${pickOpen ? "rotate-180" : ""}`} />
              </button>
              {pickOpen && (
                <div className="mt-2 rounded-[14px] border border-border bg-card p-2.5 space-y-2" data-testid="abs-people-list">
                  <div className="h-10 rounded-[10px] border border-border px-3 flex items-center gap-2">
                    <Search size={15} className="text-muted-foreground shrink-0" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat jméno…" data-testid="abs-people-search"
                      className="bg-transparent focus:outline-none w-full min-w-0 text-sm" />
                  </div>
                  <div className="flex gap-3 text-[13px] font-semibold px-1">
                    <button type="button" className="text-brand" onClick={() => set({ user_ids: list.map((p) => p.id) })} data-testid="abs-people-all">Vybrat {q ? "nalezené" : "všechny"}</button>
                    {m.user_ids.length > 0 && <button type="button" className="text-muted-foreground" onClick={() => set({ user_ids: [] })}>Zrušit výběr</button>}
                  </div>
                  <div className="max-h-52 overflow-y-auto overscroll-contain">
                    {list.map((p) => {
                      const on = m.user_ids.includes(p.id);
                      return (
                        <button key={p.id} type="button" onClick={() => toggle(p.id)} data-testid={`abs-person-${p.id}`}
                          className={`w-full min-h-[42px] px-2 rounded-[10px] flex items-center gap-2.5 text-left text-sm font-semibold ${on ? "bg-secondary" : "hover:bg-secondary/60"}`}>
                          <span className={`w-5 h-5 rounded-[6px] border-2 grid place-items-center shrink-0 ${on ? "bg-brand border-brand text-white" : "border-border"}`}>{on && <Check size={13} strokeWidth={3} />}</span>
                          <span className="truncate">{p.name}</span>
                        </button>
                      );
                    })}
                    {list.length === 0 && <div className="text-sm text-muted-foreground p-2">Nikoho takového nenacházím.</div>}
                  </div>
                  <div className="flex justify-end"><Button type="button" variant="outline" size="sm" onClick={() => setPickOpen(false)} className="w-full md:w-auto">Hotovo</Button></div>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="min-w-0"><Label>Od</Label>
              <Input type="date" value={m.date_from} onChange={(e) => e.target.value && set({ date_from: e.target.value })} className="h-11 mt-1 w-full min-w-0" data-testid="abs-from" /></div>
            <div className="min-w-0"><Label>Do</Label>
              <Input type="date" value={m.date_to} min={m.date_from} onChange={(e) => e.target.value && set({ date_to: e.target.value })} className="h-11 mt-1 w-full min-w-0" data-testid="abs-to" /></div>
          </div>

          <div>
            <Label>Typ</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {ABSENCE_TYPES.map((x) => {
                const locked = retro && ADVANCE_TYPES.includes(x.value);
                return (
                  <button key={x.value} type="button" onClick={() => set({ type: x.value })} disabled={locked} data-testid={`abs-type-${x.value}`}
                    title={locked ? "Hlásí se předem — zpětně zapíše vedoucí" : undefined}
                    className={`h-9 px-3.5 rounded-full text-[13px] font-bold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${m.type === x.value ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:text-foreground"}`}>
                    {x.label}
                  </button>
                );
              })}
            </div>
            {m.type === "neplacene_volno" && (
              <div className={`mt-2.5 flex items-start gap-3 rounded-[14px] border px-3.5 py-3 ${m.makeup ? "border-brand/50 bg-brand/5" : "border-border bg-card"}`} data-testid="abs-makeup">
                <Switch id="abs-makeup-sw" checked={!!m.makeup} onCheckedChange={(v) => set({ makeup: v })} className="mt-0.5 shrink-0" data-testid="abs-makeup-switch" />
                <label htmlFor="abs-makeup-sw" className="min-w-0 cursor-pointer">
                  <span className="block text-sm font-bold">{manager ? "Napracuje si to" : "Napracuji si to"}</span>
                  <span className="block text-[13px] text-muted-foreground leading-snug mt-0.5">
                    {manager ? "Hodiny odpracuje" : "Hodiny odpracuji"} jindy v {monthLoc}, třeba o víkendu. Měsíční fond se nesníží, přesčas se počítá až nad něj.</span>
                </label>
              </div>
            )}
            {retro && <p className="text-xs text-muted-foreground mt-1.5" data-testid="abs-retro-hint">Dovolená a náhradní volno se hlásí předem. Zpětně jde jen lékař, nemoc nebo neplacené volno — dovolenou do minulosti zapíše vedoucí.</p>}
          </div>

          <div className="space-y-2">
            <Label>Rozsah</Label>
            {seg([{ value: "full", label: "Celý den" }, { value: "half", label: "Půl dne" }, { value: "time", label: "Čas od–do" }],
              m.part, (v) => set({ part: v }), (v) => v !== "full" && !single)}
            {!single && <p className="text-xs text-muted-foreground">Půl dne a čas jdou jen u jednoho dne.</p>}
            {m.part === "half" && seg([{ value: "am", label: "Dopoledne" }, { value: "pm", label: "Odpoledne" }], m.half, (v) => set({ half: v }))}
            {m.part === "time" && (
              <div className="grid grid-cols-2 gap-3">
                <div className="min-w-0"><Label>Čas od</Label><div className="mt-1"><TimeField value={m.t_from} onChange={(v) => set({ t_from: v })} testid="abs-tfrom" label="Čas od" /></div></div>
                <div className="min-w-0"><Label>Čas do</Label><div className="mt-1"><TimeField value={m.t_to} onChange={(v) => set({ t_to: v })} testid="abs-tto" label="Čas do" /></div></div>
              </div>
            )}
          </div>

          <div>
            <Label>Poznámka {manager ? "" : "pro vedení"} <span className="text-muted-foreground font-normal">(nepovinné)</span></Label>
            <Textarea value={m.note} onChange={(e) => set({ note: e.target.value })} rows={2} maxLength={500} className="mt-1" data-testid="abs-note"
              placeholder={m.type === "lekar" ? "např. zubař" : m.type === "dovolena" ? "např. rodinná akce" : ""} />
          </div>

          <div className="rounded-[14px] bg-secondary px-3.5 py-3 text-sm" data-testid="abs-summary">
            <b>{t.label} · {whenTxt} · {amount}</b>
            {makeup && <div className="mt-0.5 flex items-center gap-1.5 text-muted-foreground" data-testid="abs-summary-makeup"><RotateCcw size={13} className="shrink-0" /> {manager ? "napracuje" : "napracuji"} v {monthLoc} · fond se nesníží</div>}
            {manager && names.length > 0 && <div className="text-muted-foreground mt-0.5 truncate">{pickLabel}</div>}
          </div>

          <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="h-11 w-full md:w-auto">Zrušit</Button>
            <Button type="button" onClick={submit} disabled={busy} className="h-11 w-full md:w-auto font-bold" data-testid="abs-submit">{primary}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const plural = (n) => (n === 1 ? "1 člověk" : n >= 2 && n <= 4 ? `${n} lidé` : `${n} lidí`);
